import React, { Component } from 'react';
import PropTypes from 'prop-types';
import Select from 'react-select';
import { Button } from '@material-ui/core';
import { stationModels } from '../../constants/stationModels';
import { isEmployee } from '../../utils';

const normalizeVersion = version => (version || '').trim();

const formatDate = date => {
    if (!date) return '';

    const parsed = new Date(date);
    if (Number.isNaN(parsed.getTime())) return '';

    return parsed.toLocaleString('ru-RU');
};

class RelinkUpdateBlock extends Component {
    state = {
        hasAccess: false,
        accessLoading: false,
        files: [],
        filesLoaded: false,
        filesLoading: false,
        selectedFile: null,
        versionLoading: false,
        updateLoading: false,
    };

    componentDidMount() {
        this.isUnmounted = false;
        this.checkAccess();
    }

    componentDidUpdate(prevProps) {
        const prevSno = prevProps.station ? prevProps.station.sno : null;
        const currentSno = this.props.station ? this.props.station.sno : null;

        if (
            prevSno !== currentSno ||
            prevProps.permissions !== this.props.permissions
        ) {
            this.setState(
                {
                    hasAccess: false,
                    accessLoading: false,
                    files: [],
                    filesLoaded: false,
                    filesLoading: false,
                    selectedFile: null,
                    versionLoading: false,
                    updateLoading: false,
                },
                this.checkAccess,
            );
        }
    }

    componentWillUnmount() {
        this.isUnmounted = true;
    }

    isRelinkStation = () => {
        const { station } = this.props;

        return station && station.model === stationModels.relink_station;
    };

    getCurrentVersion = () => {
        const { station, versionField } = this.props;

        return station ? station[versionField] : null;
    };

    getCurrentVersionUpdated = () => {
        const { station, versionUpdatedField } = this.props;

        return station ? station[versionUpdatedField] : null;
    };

    checkAccess = () => {
        const { permissions, station, canAccess } = this.props;

        if (!this.isRelinkStation()) return;

        if (isEmployee(permissions)) {
            this.setState({ hasAccess: true });
            return;
        }

        if (permissions !== 'franchisee') return;

        const sno = station.sno;
        this.setState({ accessLoading: true });
        canAccess(sno)
            .then(hasAccess => {
                if (
                    this.isUnmounted ||
                    !this.props.station ||
                    this.props.station.sno !== sno
                ) {
                    return;
                }

                this.setState({
                    hasAccess,
                    accessLoading: false,
                });
            })
            .catch(() => {
                if (!this.isUnmounted) {
                    this.setState({
                        hasAccess: false,
                        accessLoading: false,
                    });
                }
            });
    };

    isCurrentVersion = file => {
        if (!file) return false;

        const fileVersion = normalizeVersion(file.version);
        const currentVersion = normalizeVersion(this.getCurrentVersion());

        return !!fileVersion && !!currentVersion && fileVersion === currentVersion;
    };

    getFileMeta = file => {
        const { fileMetaField } = this.props;

        return file && fileMetaField ? file[fileMetaField] : undefined;
    };

    getFileLabel = file => {
        const version = file.version || 'без версии';
        const fileMeta = this.getFileMeta(file);
        const fileName = fileMeta ? ` (${fileMeta})` : '';

        return `${version}${fileName}${this.isCurrentVersion(file) ? ' - Текущая' : ''}`;
    };

    handleLoadFiles = () => {
        this.setState({ filesLoading: true });

        return this.props
            .loadFiles()
            .then(files => {
                if (this.isUnmounted) return;

                this.setState({
                    files,
                    filesLoaded: true,
                    filesLoading: false,
                    selectedFile:
                        files.find(file => !this.isCurrentVersion(file)) || files[0] || null,
                });
            })
            .catch(() => {
                if (!this.isUnmounted) {
                    this.setState({ filesLoading: false });
                }
            });
    };

    handleGetVersion = () => {
        const { station, getVersion } = this.props;
        const { versionLoading } = this.state;

        if (versionLoading) return Promise.resolve();

        this.setState({ versionLoading: true });
        return getVersion(station.sno)
            .catch(() => {})
            .finally(() => {
                if (!this.isUnmounted) {
                    this.setState({ versionLoading: false });
                }
            });
    };

    handleUpdate = () => {
        const { station, updateVersion } = this.props;
        const { filesLoaded, selectedFile } = this.state;
        const hasCurrentVersion = !!normalizeVersion(this.getCurrentVersion());

        if (!filesLoaded) {
            const versionRequest = hasCurrentVersion
                ? Promise.resolve()
                : this.handleGetVersion();

            return versionRequest.then(() => this.handleLoadFiles());
        }

        if (!hasCurrentVersion) {
            return this.handleGetVersion();
        }

        if (!selectedFile || this.isCurrentVersion(selectedFile)) return Promise.resolve();

        this.setState({ updateLoading: true });
        return updateVersion(station.sno, selectedFile.id)
            .then(() => {
                if (!this.isUnmounted) {
                    this.setState({ selectedFile: null });
                }
            })
            .catch(() => {})
            .finally(() => {
                if (!this.isUnmounted) {
                    this.setState({ updateLoading: false });
                }
            });
    };

    formatOptionLabel = file => (
        <div className="firmware-file-option">
            <span>
                {file.version || 'без версии'}
                {this.getFileMeta(file) ? (
                    <span className="firmware-file-name"> {this.getFileMeta(file)}</span>
                ) : null}
            </span>
            {this.isCurrentVersion(file) && (
                <span className="firmware-current-badge">Текущая</span>
            )}
        </div>
    );

    render() {
        const {
            permissions,
            title,
            idPrefix,
            fileLabel,
        } = this.props;
        const {
            hasAccess,
            accessLoading,
            files,
            filesLoaded,
            filesLoading,
            selectedFile,
            versionLoading,
            updateLoading,
        } = this.state;

        if (!this.isRelinkStation()) return null;
        if (!isEmployee(permissions) && permissions !== 'franchisee') return null;

        if (accessLoading) {
            return (
                <div className="station-control firmware-update testStation-update-block">
                    <div className="station-control-label">{title}</div>
                    <div className="firmware-muted">Проверяем доступ...</div>
                </div>
            );
        }

        if (!hasAccess) return null;

        const currentVersionRaw = this.getCurrentVersion();
        const hasCurrentVersion = !!normalizeVersion(currentVersionRaw);
        const currentVersion = hasCurrentVersion ? currentVersionRaw : 'Неизвестно';
        const selectedIsCurrent = this.isCurrentVersion(selectedFile);
        const updateDisabled =
            filesLoaded &&
            (!selectedFile ||
                selectedIsCurrent ||
                updateLoading ||
                versionLoading ||
                !hasCurrentVersion);

        return (
            <div className="station-control firmware-update testStation-update-block">
                <div className="station-control-label">{title}</div>

                <div className="firmware-fields-row">
                    <div className="firmware-field">
                        <div className="firmware-field-header">
                            <label htmlFor={`${idPrefix}_current_version`}>Текущая версия</label>
                            {this.getCurrentVersionUpdated() && (
                                <span className="firmware-muted">
                                    Актуально на: {formatDate(this.getCurrentVersionUpdated())}
                                </span>
                            )}
                        </div>
                        <div className="firmware-version-row">
                            <input
                                id={`${idPrefix}_current_version`}
                                className={`firmware-version-input${
                                    hasCurrentVersion ? '' : ' firmware-version-input_unknown'
                                }`}
                                value={currentVersion}
                                readOnly
                            />
                            <Button
                                className="firmware-version-button"
                                variant="outlined"
                                color="primary"
                                disabled={versionLoading}
                                onClick={this.handleGetVersion}
                            >
                                {versionLoading ? 'Получаем...' : 'Получить'}
                            </Button>
                        </div>
                        {!hasCurrentVersion && (
                            <div className="firmware-warning">
                                Обновление доступно только после получения текущей версии.
                            </div>
                        )}
                    </div>

                    {filesLoaded && (
                        <div className="firmware-field firmware-file-field">
                            <label htmlFor={`${idPrefix}_file`}>{fileLabel}</label>
                            <div className="firmware-update-row">
                                <Select
                                    inputId={`${idPrefix}_file`}
                                    className="firmware-file-select"
                                    options={files}
                                    value={selectedFile}
                                    onChange={nextFile => this.setState({ selectedFile: nextFile })}
                                    getOptionLabel={this.getFileLabel}
                                    getOptionValue={file => String(file.id)}
                                    formatOptionLabel={this.formatOptionLabel}
                                    isLoading={filesLoading}
                                    placeholder="Выберите файл"
                                    noOptionsMessage={() => 'Нет доступных файлов'}
                                />
                                <Button
                                    variant="contained"
                                    color="primary"
                                    disabled={filesLoading || updateDisabled}
                                    onClick={this.handleUpdate}
                                >
                                    {updateLoading ? 'Запускаем...' : 'Запустить обновление'}
                                </Button>
                            </div>
                        </div>
                    )}

                    {!filesLoaded && (
                        <div className="firmware-field firmware-file-field">
                            <label>{fileLabel}</label>
                            <Button
                                variant="contained"
                                color="primary"
                                disabled={filesLoading}
                                onClick={this.handleUpdate}
                            >
                                {filesLoading ? 'Загружаем...' : 'Обновить'}
                            </Button>
                        </div>
                    )}
                </div>
            </div>
        );
    }
}

RelinkUpdateBlock.propTypes = {
    permissions: PropTypes.string,
    station: PropTypes.object,
    canAccess: PropTypes.func,
    getVersion: PropTypes.func,
    loadFiles: PropTypes.func,
    updateVersion: PropTypes.func,
    title: PropTypes.string,
    idPrefix: PropTypes.string,
    versionField: PropTypes.string,
    versionUpdatedField: PropTypes.string,
    fileLabel: PropTypes.string,
    fileMetaField: PropTypes.string,
};

export default RelinkUpdateBlock;
